import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/auth';
import notify from 'devextreme/ui/notify';
import { useNavigate, useParams } from 'react-router-dom';
import {getAPIData, adminGetCompanies} from '../../api/api'
import { LoadIndicator } from 'devextreme-react/load-indicator';
import { DataGrid, Column, HeaderFilter,
  Paging, Export, Grouping,
} from 'devextreme-react/data-grid';
import { Button } from 'devextreme-react/button';
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver';
import { exportDataGrid as xlsExportDataGrid } from 'devextreme/excel_exporter';
import { SelectBox } from 'devextreme-react/select-box';
import './hse.scss';

export default function HSECompanyDataDetails() {
  let { year, company_id } = useParams();
  const navigate = useNavigate();
  const { user, refreshSession } = useAuth();
  const [originalData, setOriginalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [companiesList, setCompaniesList] = useState(null);
  const [yearsList, setYearsList] = useState([]);
  const [currentCompany, setCurrentCompany] = useState(null);
  const [currentYear, setCurrentYear] = useState(null);

  const onYearSelected = useCallback((e) => {
    setOriginalData(null);
    setCurrentYear(e.value);
  }, [])

  const onGetCompaniesSuccess = useCallback((data) => {
    data = data.sort((a,b) => (a.company_name > b.company_name) ? 1 : ((b.company_name > a.company_name) ? -1 : 0))
    setCompaniesList(data);
    if (yearsList.length === 0) {
      const currentYear = new Date().getFullYear();
      for (var i=2023; i<currentYear; i++) {
        yearsList.push(i.toString())
      }
      setYearsList([...yearsList]);
    }
    if (year) {
      onYearSelected({value: year.toString()});
    }
    if (company_id) {
      setOriginalData(null);
      for (i=0; i<data.length; i++){
        if (data[i].company_id === company_id) {
          setCurrentCompany(data[i]);
        }
      }
    }
    setLoading(false);
  }, [setCompaniesList, setLoading, yearsList, setYearsList, company_id, onYearSelected, year])

  const onGetDataSuccess = useCallback((data) => {
    console.log(data);
    if (data.items) {
      setOriginalData(data.items);
      setLoading(false);
    } else {
      notify("The selected company has no data.", 'warning', 5000);
      year?navigate("/hsedata/"+year):navigate("/hsedata");
    }
  }, [setOriginalData, navigate, year]);

  const onError = useCallback((error) => {
    if (error.status === 401) {
      refreshSession();
    } else {
      notify("An error has occured. Please try again later.", 'error', 2000);
      console.log(error);
      setLoading(false);
    }
  }, [refreshSession]);

  const onCompanySelected = useCallback((e) => {
    if (!companiesList) {
      return
    }
    setOriginalData(null);
    for (var i=0; i<companiesList.length; i++){
      if (companiesList[i].company_id === e.value) {
        setCurrentCompany(companiesList[i]);
      }
    }
  }, [companiesList])

  useEffect(() => {
    if (user.signInUserSession) {
      let groups = user.signInUserSession.idToken.payload['cognito:groups']
      var has_perm=false;
      if (groups) {
        for (var i=0; i<groups.length; i++) {
          if (groups[i]==='admin') {
            has_perm=true;
            setIsAdmin(true);
            if (companiesList === null) {
              adminGetCompanies(user, onGetCompaniesSuccess, onError);
            }
            break;
          }
        }
      }
      if (!has_perm) {
        notify("Insufficient permissions. Please contact the system administrator.", 'error', 5000);
        navigate('/home');
        return
      }
    }
  }, [user, navigate, onGetCompaniesSuccess, onError, setIsAdmin, companiesList]);

  useEffect(() => {
    if (currentCompany && currentYear) {
      getAPIData("/reports/data/hse/"+currentCompany['company_id']+"/"+currentYear, user, onGetDataSuccess, onError);
    }
  }, [currentCompany, currentYear, user, onGetDataSuccess, onError, ]);

  const onExporting = useCallback((e) => {
    if (e.format === "xlsx") {
      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet('Main sheet');

      xlsExportDataGrid({
        component: e.component,
        worksheet,
        autoFilterEnabled: true,
      }).then(() => {
        workbook.xlsx.writeBuffer().then((buffer) => {
          saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'AXPC-SIF-Events.xlsx');
        });
      });
    }
  }, []);


  if(!loading) {
    return (
      <React.Fragment>
        <div className="d-flex flex-row justify-content-between">
          <h2 className={'content-block'}>Health, Safety & Environmental Company Data</h2>
          <div className="d-flex flex-row justify-content-between">
            <Button
              className="me-2"
              text="Back"
              stylingMode="outlined"
              type="default"
              value={currentCompany?.company_id}
              onClick={() => {year?navigate("/hsedata/"+year):navigate("/hsedata")}}
            />
          </div>
        </div>
        <hr className="mb-4"/>
        {isAdmin && (!year) && <div className="mb-2">
        <h5 className="mb-2"> Choose the reporting year</h5>
        <SelectBox
            dataSource={yearsList}
            showDropDownButton={true}
            activeStateEnabled={true}
            onValueChanged={onYearSelected}
            value={currentYear}
        >
        </SelectBox>
        </div>
        }

        {isAdmin && (!company_id) && <div className="content-block mt-5 mb-2">
        <h5 className="mb-2"> Choose the company you want to see</h5>
        <SelectBox
            dataSource={companiesList}
            showDropDownButton={true}
            activeStateEnabled={true}
            searchEnabled={true}
            valueExpr='company_id'
            displayExpr='company_name'
            onValueChanged={onCompanySelected}
        >
        </SelectBox>
        </div>

        }

        {isAdmin && company_id && <h5 className="mt-3"> Company: {currentCompany?.company_name}</h5>}
        {isAdmin && year && <h5 className="mt-3 mb-1"> Reporting year: {currentYear}</h5>}

        {originalData ? <div className={'content-block'}>
          <DataGrid id="dataGrid"
            dataSource={originalData}
            columnHidingEnabled={true}
            cellHintEnabled={true}
            wordWrapEnabled={true}
            onExporting={onExporting}
          >
            <Export enabled={true} formats={['xlsx']} />
            <HeaderFilter visible={true} />
            <Grouping autoExpandAll={false} />
            <Column
              dataField={'Identifier'}
              defaultSortOrder="asc"
              allowSorting={true}
              visibleIndex={0}
              dataType="number"
              width="120px"
            >
            </Column>
            <Column
              dataField={'Category'}
              visibleIndex={1}
              width="120px"
            >
            </Column>
            <Column
              dataField={'Subcategory'}
              visibleIndex={2}
              width="200px"
            >
            </Column>
            <Column
              dataField={'Details'}
              visibleIndex={3}
              width="350px"
            >
            </Column>
            {originalData && originalData[0] &&
              Object.keys(originalData[0]).filter(e => e.includes("year_")).sort().map((e, idx) => (
                <Column
                dataField={e}
                width="120px"
                visibleIndex={4+idx}
              >
              </Column>))}

            <Paging defaultPageSize={200} />
          </DataGrid>
        </div>:
        <div className="d-flex flex-row justify-content-center align-items-center mt-5 pt-5">
            <LoadIndicator
                id="large-indicator"
                height={60}
                width={60}
            />
        </div>
        }
      </React.Fragment>
    )
  } else {
    return (
      <div className="d-flex flex-row justify-content-center align-items-center mt-5 pt-5">
          <LoadIndicator
              id="large-indicator"
              height={60}
              width={60}
          />
      </div>
    )
  }

}
